import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button as ChakraButton,
  Flex,
  HStack,
  Icon,
  MenuItem,
  MenuList,
  Spinner,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import { FiSearch } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { BsEnvelopePlus, BsKey } from 'react-icons/bs';
import { useHistory } from 'react-router';
import download from 'downloadjs';
import {
  Button,
  ColorfulBar,
  Heading,
  Input,
  Menu,
  Pagination,
  StatusBadge,
  Table,
} from '~/components';
import { PAGE_SIZE, ROUTER_PATHS } from '~/constants';
import { useDebounce, useGenericUsers, useQueryParams } from '~/hooks';
import { useRoleCan } from '~/hooks/useCan';
import { RolesEnum } from '~/utils/roles';
import { queryClient } from '~/utils/react-query';
import api from '~/utils/api';
import { updateUrlParams } from '~/utils/updateUrlParams';
import { getUsersXlsx } from '~/services/Users';

export const Users: React.VFC = () => {
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();

  const [searchTmp, setSearchTmp] = useState(() => {
    return queryParams.get('busca') ?? '';
  });
  const [parametros, setParametros] = useState(() => {
    return {
      busca: queryParams.get('busca') ?? '',
      pagina: queryParams.get('pagina') ?? '1',
      paginacao: queryParams.get('paginacao') ?? PAGE_SIZE.toString(),
    };
  });

  const { data, isLoading, isFetching, error, refetch } = useGenericUsers(
    Number(parametros.pagina),
    parametros.busca,
    Number(parametros.paginacao),
  );
  const toast = useToast();
  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const isAdmin = useRoleCan({ roles: [RolesEnum.ADMINISTRADOR] });
  const [usuarioReset, setUsuarioReset] = useState<{
    nome: string;
    email: string;
  } | null>(null);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const cancelResetRef = useRef<HTMLButtonElement>(null);

  useDebounce(
    () => {
      setParametros({ ...parametros, busca: searchTmp, pagina: '1' });
      updateUrlParams(
        { ...parametros, busca: searchTmp, pagina: '1' },
        history,
      );
      refetch();
    },
    1000,
    [searchTmp],
  );

  useEffect(() => {
    const pagina = queryParams.get('pagina');
    setParametros({ ...parametros, pagina: pagina ?? '1' });
    updateUrlParams({ ...parametros, pagina: pagina ?? '1' }, history);
  }, [location.search]);

  useEffect(() => {
    refetch();
  }, []);

  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();

  function handleDialog() {
    onDialogOpen();
  }

  function handleSendEmail(id_usuario: number) {
    const request = api.get(`/user_customer/${id_usuario}/resend-email`);
    request
      .then(() => {
        handleDialog();
        queryClient.invalidateQueries('users');
      })
      .catch(err => {
        if (err.response.status === 403) {
          toast({
            title: 'Usuário sem permissão',
            description: 'Sem permissão para execução da operação.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        } else if (err.response.status === 400) {
          const errorsValidate = Object.entries(err.response.data);

          errorsValidate.forEach(errors => {
            toast({
              title: `Erro ao reenviar e-mail`,
              description: String(err[1]),
              status: 'error',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          });
        }
      })
      .finally(() => {
        refetch();
      });
  }

  function handleSendResetPassword() {
    if (!usuarioReset) return;
    const { email } = usuarioReset;

    setIsSendingReset(true);
    api
      .post('/auth/forgot-password', { email })
      .then(() => {
        setUsuarioReset(null);
        toast({
          title: `E-mail de redefinição de senha enviado para ${email}.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .catch(err => {
        setUsuarioReset(null);
        toast({
          title: 'Erro ao enviar e-mail de redefinição de senha',
          description:
            err?.response?.data?.email?.[0] ?? err?.response?.data?.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsSendingReset(false));
  }

  const handlePageSizeChange = (e: any) => {
    setParametros({ ...parametros, paginacao: e.target.value, pagina: '1' });
    updateUrlParams(
      { ...parametros, paginacao: e.target.value, pagina: '1' },
      history,
    );
  };

  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getUsersXlsx(parametros.busca)
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        // recuperando o erro do back
        // https://github.com/axios/axios/issues/3779
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };
  return (
    <Box px="8" py="4">
      <Heading>Usuários</Heading>
      <Flex pt={2} flexDir="column" w="100%">
        <Flex justify="space-between" py={5}>
          <Flex align="center" gridGap={4}>
            <Input
              name="search"
              type="text"
              icon={FiSearch}
              value={searchTmp}
              onChange={e => setSearchTmp(e.target.value)}
              placeholder="Procure por nome, login ou email"
              w={350}
              fontSize="15px"
            />
            {isFetching && <Spinner />}
          </Flex>
          <HStack
            gridGap={2}
            justifyContent="space-between"
            alignItems="baseline"
          >
            <Link to={`${ROUTER_PATHS.PORTAL.ADMIN.USERS_FORM}`}>
              <Button>Cadastrar</Button>
            </Link>
            <Button
              disabled={isLoadingDownload || isLoading}
              onClick={handleDownload}
              color="white"
              background="gray.900"
              _hover={{
                bgColor: 'black',
              }}
              style={{ marginTop: '20px' }}
            >
              Exportar
              {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
            </Button>
          </HStack>
        </Flex>
        {isLoading ? (
          <Flex justify="center">
            <Spinner />
          </Flex>
        ) : error ? (
          <Flex justify="center">
            <Text>Falha ao obter dados dos usuários.</Text>
          </Flex>
        ) : (
          <>
            <Table>
              <Thead>
                <Tr>
                  <Th>Nome</Th>
                  <Th>Email</Th>
                  <Th>Perfil de Acesso</Th>
                  <Th>Tipo de usuário</Th>
                  <Th>Tipo de Função</Th>
                  <Th>Status</Th>
                  <Th>Bloqueado</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {!!data?.results.length &&
                  data?.results.map(user => (
                    <Tr key={`${user.id_usuario}-${user.tipo_usuario}`}>
                      <Td>
                        <Link
                          to={`/portal/admin/users/form?userId=${user.id_usuario}&type=${user.tipo_usuario}`}
                        >
                          <Text
                            _hover={{
                              color: 'gray.900',
                              textDecoration: 'underline',
                            }}
                          >
                            {user.nome_usuario || '-'}
                          </Text>
                        </Link>
                      </Td>
                      <Td>{user.email_usuario}</Td>
                      <Td>{user.perfil_acesso}</Td>
                      <Td>{user.tipo_usuario}</Td>
                      <Td>{user.nm_funcao || '-'}</Td>
                      <Td>
                        <StatusBadge
                          variant={user.tp_situacao ? 'green' : 'red'}
                        >
                          {user.tp_situacao ? 'Ativo' : 'Inativo'}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <StatusBadge variant={user.bloqueado ? 'red' : 'green'}>
                          {user.bloqueado ? 'SIM' : 'NÃO'}
                        </StatusBadge>
                      </Td>
                      <Td whiteSpace="nowrap">
                        {!user.tp_situacao && user.bloqueado && (
                          <Tooltip
                            hasArrow
                            label="Reenviar e-mail de primeiro acesso."
                            bg="gray"
                            shouldWrapChildren
                            placement="left-start"
                          >
                            <Badge
                              colorScheme="green"
                              px={2}
                              py={1}
                              borderRadius={4}
                              w="32px"
                              textAlign="center"
                              fontSize="16px"
                              cursor="pointer"
                              marginRight="30px"
                              onClick={() => handleSendEmail(user.id_usuario)}
                            >
                              <Icon as={BsEnvelopePlus} />
                            </Badge>
                          </Tooltip>
                        )}
                        {isAdmin &&
                          user.tipo_usuario === 'Cliente' &&
                          user.tp_situacao &&
                          !user.bloqueado && (
                            <Tooltip
                              hasArrow
                              label="Enviar e-mail de redefinição de senha."
                              bg="gray"
                              shouldWrapChildren
                              placement="left-start"
                            >
                              <Badge
                                colorScheme="blue"
                                px={2}
                                py={1}
                                borderRadius={4}
                                w="32px"
                                textAlign="center"
                                fontSize="16px"
                                cursor="pointer"
                                marginRight="30px"
                                aria-label="Enviar e-mail de redefinição de senha"
                                data-testid="btn-reset-senha"
                                onClick={() =>
                                  setUsuarioReset({
                                    nome: user.nome_usuario,
                                    email: user.email_usuario,
                                  })
                                }
                              >
                                <Icon as={BsKey} />
                              </Badge>
                            </Tooltip>
                          )}
                        <Menu>
                          <MenuList>
                            <Link
                              to={`/portal/admin/users/form?userId=${user.id_usuario}&type=${user.tipo_usuario}`}
                            >
                              <MenuItem>Editar</MenuItem>
                            </Link>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            <Flex justify="flex-end">
              {data && (
                <Pagination
                  totalCountOfRegisters={data.count}
                  currentPage={Number(parametros.pagina) ?? 1}
                  onPageChange={_page => {
                    setParametros({ ...parametros, pagina: _page.toString() });
                    updateUrlParams(
                      { ...parametros, pagina: _page.toString() },
                      history,
                    );
                  }}
                  onPageSizeChange={handlePageSizeChange}
                  registersPerPage={Number(parametros.paginacao) ?? PAGE_SIZE}
                />
              )}
            </Flex>
          </>
        )}
      </Flex>

      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={undefined}
        onClose={onDialogClose}
        isOpen={isDialogOpen}
        isCentered
        closeOnOverlayClick={false}
        size="xl"
      >
        <AlertDialogOverlay />

        <AlertDialogContent width={800}>
          <ColorfulBar />
          <AlertDialogHeader>Mensagem</AlertDialogHeader>
          <AlertDialogBody>Seu e-mail foi enviado com sucesso.</AlertDialogBody>
          <AlertDialogFooter>
            <Button
              onClick={() => {
                onDialogClose();
              }}
              variant="primary"
              width={100}
            >
              OK
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelResetRef}
        onClose={() => setUsuarioReset(null)}
        isOpen={usuarioReset !== null}
        isCentered
        closeOnOverlayClick={false}
        size="xl"
      >
        <AlertDialogOverlay />

        <AlertDialogContent width={800}>
          <ColorfulBar />
          <AlertDialogHeader>Redefinição de senha</AlertDialogHeader>
          <AlertDialogBody>
            Deseja realmente enviar o e-mail de redefinição de senha para{' '}
            <strong>{usuarioReset?.nome}</strong> ({usuarioReset?.email})?
          </AlertDialogBody>
          <AlertDialogFooter gridGap={2}>
            <ChakraButton
              ref={cancelResetRef}
              onClick={() => setUsuarioReset(null)}
              disabled={isSendingReset}
              width={100}
              fontSize="14px"
            >
              Cancelar
            </ChakraButton>
            <Button
              onClick={() => handleSendResetPassword()}
              variant="primary"
              isLoading={isSendingReset}
              disabled={isSendingReset}
              width={100}
            >
              Confirmar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};
